import { useCallback } from 'react'
import { ConnectorNames, connectorLocalStorageKey } from '@pancakeswap-libs/uikit'
import { AbstractConnector } from '@web3-react/abstract-connector'
import { useAddPopup } from '../state/application/hooks'
import { SUPPORTED_CHAIN_IDS, bscConnector, injected, isWalletConnectConfigured } from '../connectors'
import { Eip6963Connector } from '../connectors/Eip6963Connector'
import { findEip6963ProviderByRdns, getEip6963Providers } from '../connectors/eip6963'
import { getLastWalletId, setLastWalletId } from '../connectors/lastWallet'
import useWalletActivate from './useWalletActivate'

/**
 * Adapter for the UIKit's own Menu/WalletModal, which is a compiled component that
 * renders a fixed roster of seven wallet tiles but can only report three connector
 * ids back (`injected`, `walletconnect`, `bsc`).
 *
 * Previously the callback did `if (connector) activate(connector)` against a map
 * holding only two entries, so picking WalletConnect did nothing at all - no
 * connection, no error, no feedback. This resolves every id to either a working
 * connector or an explicit message.
 *
 * The app's own connect flow (components/WalletModal) is not constrained this way and
 * offers the full discovered wallet list; this exists only to keep the UIKit-rendered
 * nav button honest until that nav is replaced.
 */
export default function useUikitWalletLogin(): (connectorId: ConnectorNames) => void {
  const activate = useWalletActivate()
  const addPopup = useAddPopup()

  return useCallback(
    (connectorId: ConnectorNames) => {
      if (connectorId === ConnectorNames.WalletConnect && !isWalletConnectConfigured) {
        addPopup(
          {
            walletError: {
              message:
                'WalletConnect is not available yet. Connect with a browser wallet extension for now.',
            },
          },
          `wallet-error-${Date.now()}`
        )
        return
      }

      let connector: AbstractConnector = injected
      let walletId: string = connectorId

      if (connectorId === ConnectorNames.BSC) {
        connector = bscConnector
      } else if (connectorId === ConnectorNames.Injected) {
        // The UIKit collapses MetaMask / TrustWallet / MathWallet / TokenPocket /
        // SafePal into this single id, so it cannot tell us which one was clicked.
        // Prefer the wallet this user connected with last, then any single detected
        // wallet, and only then fall back to the shared `window.ethereum` slot.
        const discovered = getEip6963Providers()
        const remembered = getLastWalletId()
        const detail =
          (remembered ? findEip6963ProviderByRdns(remembered) : undefined) ??
          (discovered.length === 1 ? discovered[0] : undefined)

        if (detail) {
          connector = new Eip6963Connector({ detail, supportedChainIds: SUPPORTED_CHAIN_IDS })
          walletId = detail.info.rdns
        }
      }

      setLastWalletId(walletId)
      window.localStorage.setItem(connectorLocalStorageKey, walletId)
      activate(connector)
    },
    [activate, addPopup]
  )
}
